import { useState } from "react";
import {
  Badge,
  BottomSheet,
  Breadcrumb,
  Button,
  Code,
  Dialog,
  Pagination,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Tooltip,
} from "@one-nexty/components-web";

import { Preview } from "../ui/preview";
import { Section, SubSection } from "../ui/section";
import { TrashIcon } from "../ui/icons";

export function NavigationSection() {
  const [page, setPage] = useState(7);

  return (
    <Section
      id="navigation"
      eyebrow="Navigation"
      title="Tabs, Breadcrumb & Pagination"
      description="Motif « onglets automatiques » du WAI-ARIA APG : flèches pour naviguer, Home/Fin aux extrémités, roving tabindex."
    >
      <SubSection
        title="Onglets — soulignés"
        hint="L'indicateur glisse en translate + scaleX, sans une seule passe de mise en page"
      >
        <Preview
          layout="stack"
          label="tabs.tsx"
          code={`<Tabs defaultValue="apercu">
  <TabList aria-label="Sections du projet">
    <Tab value="apercu">Aperçu</Tab>
    <Tab value="activite" badge={<Badge tone="brand">12</Badge>}>Activité</Tab>
    <Tab value="archive" disabled>Archive</Tab>
  </TabList>

  <TabPanel value="apercu">…</TabPanel>
  <TabPanel value="activite">…</TabPanel>
</Tabs>`}
        >
          <Tabs defaultValue="apercu">
          <TabList aria-label="Sections du projet">
            <Tab value="apercu">Aperçu</Tab>
            <Tab value="activite" badge={<Badge tone="brand">12</Badge>}>
              Activité
            </Tab>
            <Tab value="parametres">Paramètres</Tab>
            <Tab value="archive" disabled>
              Archive
            </Tab>
          </TabList>
          <TabPanel value="apercu" className="text-sm text-fg-muted">
            Seul le panneau actif est monté.
          </TabPanel>
          <TabPanel value="activite" className="text-sm text-fg-muted">
            Douze événements depuis hier.
          </TabPanel>
          <TabPanel value="parametres" className="text-sm text-fg-muted">
            Réglages du projet.
          </TabPanel>
          </Tabs>
        </Preview>
      </SubSection>

      <SubSection title="Onglets — encadrés">
        <Preview
          layout="stack"
          label="tabs-enclosed.tsx"
          code={`<TabList aria-label="Période" variant="enclosed">
  <Tab value="jour">Jour</Tab>
  <Tab value="semaine">Semaine</Tab>
</TabList>`}
        >
          <Tabs defaultValue="jour">
          <TabList aria-label="Période" variant="enclosed">
            <Tab value="jour">Jour</Tab>
            <Tab value="semaine">Semaine</Tab>
            <Tab value="mois">Mois</Tab>
          </TabList>
          <TabPanel value="jour" className="text-sm text-fg-muted">
            Données du jour.
          </TabPanel>
          <TabPanel value="semaine" className="text-sm text-fg-muted">
            Données de la semaine.
          </TabPanel>
          <TabPanel value="mois" className="text-sm text-fg-muted">
            Données du mois.
            </TabPanel>
          </Tabs>
        </Preview>
      </SubSection>

      <SubSection title="Fil d'Ariane" hint="Le dernier élément n'est jamais un lien">
        <Preview
          label="breadcrumb.tsx"
          code={`<Breadcrumb
  items={[
    { label: "Accueil", href: "#" },
    { label: "Composants", href: "#" },
    { label: "Breadcrumb" },
  ]}
/>`}
        >
          <Breadcrumb
            items={[
              { label: "Accueil", href: "#" },
              { label: "Design system", href: "#" },
              { label: "Composants", href: "#" },
              { label: "Breadcrumb" },
            ]}
          />
        </Preview>
      </SubSection>

      <SubSection title="Pagination">
        <Preview
          layout="stack"
          label="pagination.tsx"
          code={`<Pagination page={page} pageCount={24} onChange={setPage} />`}
        >
          <Pagination page={page} pageCount={24} onChange={setPage} />
          <p className="text-xs text-fg-muted">Page {page} sur 24</p>
        </Preview>
      </SubSection>
    </Section>
  );
}

export function OverlaysSection() {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <Section
      id="superpositions"
      eyebrow="Superpositions"
      title="Dialog, BottomSheet & Tooltip"
      description="La modale s'anime en opacity et transform via @starting-style : entrée ET sortie, sans JavaScript. Sous 640px, Dialog bascule automatiquement en BottomSheet."
    >
      <SubSection title="Bottom Sheet" hint="Glissez la poignée vers le bas pour fermer">
        <Preview
          label="bottom-sheet.tsx"
          code={`<BottomSheet
  open={open}
  onClose={() => setOpen(false)}
  title="Options"
  footer={<Button onClick={() => setOpen(false)}>Valider</Button>}
>
  …
</BottomSheet>`}
        >
          <Button variant="primary" onClick={() => setSheetOpen(true)}>
            Ouvrir la Bottom Sheet
          </Button>
        </Preview>

        <BottomSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title="Options"
          description="Faites glisser la poignée vers le bas."
          footer={
            <>
              <Button variant="ghost" onClick={() => setSheetOpen(false)}>
                Fermer
              </Button>
              <Button onClick={() => setSheetOpen(false)}>Valider les options</Button>
            </>
          }
        >
          <ul className="space-y-2 text-xs text-fg">
            <li className="flex items-center gap-2">
              <Badge tone="success">Swipe-to-dismiss</Badge>
              Glissement tactile pour fermer.
            </li>
            <li className="flex items-center gap-2">
              <Badge tone="brand">Rubber banding</Badge>
              Amorti élastique vers le haut.
            </li>
            <li className="flex items-center gap-2">
              <Badge tone="neutral">Safe area</Badge>
              <Code>pb-safe</Code> sur iOS.
            </li>
          </ul>
        </BottomSheet>
      </SubSection>

      <SubSection title="Modale">
        <Preview
          label="dialog.tsx"
          code={`<Dialog
  open={open}
  onClose={() => setOpen(false)}
  title="Inviter un membre"
  description="Il recevra un e-mail avec un lien d'activation."
  footer={
    <>
      <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
      <Button onClick={() => setOpen(false)}>Envoyer</Button>
    </>
  }
>
  …
</Dialog>`}
        >
          <Button onClick={() => setOpen(true)}>Ouvrir une modale</Button>
          <Button variant="danger" leadingIcon={<TrashIcon />} onClick={() => setConfirmOpen(true)}>
            Supprimer le projet
          </Button>
        </Preview>

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          title="Inviter un membre"
          description="Il recevra un e-mail avec un lien d'activation."
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                Annuler
              </Button>
              <Button onClick={() => setOpen(false)}>Envoyer l'invitation</Button>
            </>
          }
        >
          Essayez <Code>Échap</Code>, le clic sur le voile et <Code>Tab</Code> : le focus ne sort
          jamais de la modale.
        </Dialog>

        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          size="sm"
          title="Supprimer ce projet ?"
          description="Cette action est irréversible."
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                Annuler
              </Button>
              <Button variant="danger" onClick={() => setConfirmOpen(false)}>
                Supprimer définitivement
              </Button>
            </>
          }
        />
      </SubSection>

      <SubSection title="Infobulles" hint="Apparaissent aussi au focus clavier">
        <Preview
          label="tooltip.tsx"
          code={`<Tooltip content="Infobulle au-dessus">
  <Button variant="outline">Haut</Button>
</Tooltip>

<Tooltip content="À droite" side="right">
  <Button variant="outline">Droite</Button>
</Tooltip>`}
        >
          <Tooltip content="Infobulle au-dessus">
            <Button variant="outline">Haut</Button>
          </Tooltip>
          <Tooltip content="Infobulle en dessous" side="bottom">
            <Button variant="outline">Bas</Button>
          </Tooltip>
          <Tooltip content="À droite" side="right">
            <Button variant="outline">Droite</Button>
          </Tooltip>
          <Tooltip content="Un texte plus long, qui passe sur plusieurs lignes.">
            <Button variant="ghost" size="icon" aria-label="Aide">
              ?
            </Button>
          </Tooltip>
        </Preview>
      </SubSection>
    </Section>
  );
}
